import React, { useState } from 'react';
import { Search, Flag, Eye, X, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';

export default function ConversationLogsTable({ conversations, filters, onFiltersChange, onFlag, onUnflag, onRefresh }) {
  const [selectedLog, setSelectedLog] = useState(null);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [flagData, setFlagData] = useState({
    type: 'incorrect',
    severity: 'medium',
    reason: '',
    description: '',
    suggestedCorrection: ''
  });

  const handleFlagSubmit = async () => {
    if (!selectedLog || !flagData?.description) {
      alert('Veuillez remplir tous les champs requis');
      return;
    }

    await onFlag(selectedLog?.id, flagData);
    setShowFlagModal(false);
    setSelectedLog(null);
    setFlagData({
      type: 'incorrect',
      severity: 'medium',
      reason: '',
      description: '',
      suggestedCorrection: ''
    });
  };

  const getConfidenceColor = (score) => {
    if (score >= 0.8) return 'text-green-600 bg-green-100';
    if (score >= 0.5) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Rechercher dans les conversations..."
              value={filters?.searchQuery}
              onChange={(e) => onFiltersChange({ ...filters, searchQuery: e?.target?.value })}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex gap-2">
          <select
            value={filters?.isFlagged === null ? 'all' : filters?.isFlagged ? 'flagged' : 'normal'}
            onChange={(e) => {
              const value = e?.target?.value === 'all' ? null : e?.target?.value === 'flagged';
              onFiltersChange({ ...filters, isFlagged: value });
            }}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
          >
            <option value="all">Tous</option>
            <option value="normal">Non signalés</option>
            <option value="flagged">Signalés</option>
          </select>

          <button
            onClick={onRefresh}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Actualiser
          </button>
        </div>
      </div>
      {/* Conversations Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Question Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Réponse Fatou</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Confiance</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {conversations?.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                    Aucune conversation trouvée
                  </td>
                </tr>
              ) : (
                conversations?.map((conv) => (
                  <tr key={conv?.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {format(new Date(conv.created_at), 'dd/MM/yyyy HH:mm')}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <div className="max-w-xs truncate" title={conv?.user_message}>
                        {conv?.user_message}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      <div className="max-w-xs truncate" title={conv?.ai_response}>
                        {conv?.ai_response}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getConfidenceColor(conv?.confidence_score)}`}>
                        {(parseFloat(conv?.confidence_score) * 100)?.toFixed(0)}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {conv?.is_flagged ? (
                        <span className="flex items-center text-red-600">
                          <Flag size={14} className="mr-1" />
                          Signalé
                        </span>
                      ) : (
                        <span className="flex items-center text-green-600">
                          <CheckCircle size={14} className="mr-1" />
                          Normal
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setSelectedLog(conv)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Voir détails"
                        >
                          <Eye size={16} />
                        </button>
                        {!conv?.is_flagged ? (
                          <button
                            onClick={() => {
                              setSelectedLog(conv);
                              setShowFlagModal(true);
                            }}
                            className="text-orange-600 hover:text-orange-800"
                            title="Signaler"
                          >
                            <Flag size={16} />
                          </button>
                        ) : (
                          <button
                            onClick={() => onUnflag(conv?.id)}
                            className="text-green-600 hover:text-green-800"
                            title="Retirer le signalement"
                          >
                            <CheckCircle size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Detail Modal */}
      {selectedLog && !showFlagModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Détails de la Conversation</h3>
                <button onClick={() => setSelectedLog(null)} className="text-gray-400 hover:text-gray-600">
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <p className="text-gray-900">{format(new Date(selectedLog.created_at), 'dd/MM/yyyy à HH:mm:ss')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Question Client</label>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-gray-900">{selectedLog?.user_message}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Réponse Fatou</label>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-gray-900">{selectedLog?.ai_response}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Score de Confiance</label>
                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${getConfidenceColor(selectedLog?.confidence_score)}`}>
                      {(parseFloat(selectedLog?.confidence_score) * 100)?.toFixed(0)}%
                    </span>
                  </div>

                  {selectedLog?.response_time_ms && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Temps de Réponse</label>
                      <p className="text-gray-900">{selectedLog?.response_time_ms}ms</p>
                    </div>
                  )}
                </div>

                {selectedLog?.is_flagged && selectedLog?.flag_reason && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Raison du Signalement</label>
                    <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                      <p className="text-red-900">{selectedLog?.flag_reason}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                {!selectedLog?.is_flagged && (
                  <button
                    onClick={() => setShowFlagModal(true)}
                    className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                  >
                    Signaler cette conversation
                  </button>
                )}
                <button
                  onClick={() => setSelectedLog(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Flag Modal */}
      {showFlagModal && selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900">Signaler une Réponse Incorrecte</h3>
                <button onClick={() => setShowFlagModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Type de Problème</label>
                  <select
                    value={flagData?.type}
                    onChange={(e) => setFlagData({ ...flagData, type: e?.target?.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    <option value="incorrect">Réponse Incorrecte</option>
                    <option value="incomplete">Réponse Incomplète</option>
                    <option value="inappropriate">Ton Inapproprié</option>
                    <option value="outdated">Information Obsolète</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Sévérité</label>
                  <select
                    value={flagData?.severity}
                    onChange={(e) => setFlagData({ ...flagData, severity: e?.target?.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    <option value="low">Faible</option>
                    <option value="medium">Moyenne</option>
                    <option value="high">Haute</option>
                    <option value="critical">Critique</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description du Problème *</label>
                  <textarea
                    value={flagData?.description}
                    onChange={(e) => setFlagData({ ...flagData, description: e?.target?.value })}
                    rows={4}
                    placeholder="Décrivez le problème avec la réponse de Fatou..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Correction Suggérée</label>
                  <textarea
                    value={flagData?.suggestedCorrection}
                    onChange={(e) => setFlagData({ ...flagData, suggestedCorrection: e?.target?.value })}
                    rows={4}
                    placeholder="Proposez une meilleure réponse..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowFlagModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Annuler
                </button>
                <button
                  onClick={handleFlagSubmit}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Signaler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}